import {
  CheckCircle,
  PauseCircle,
  Clock,
  XCircle,
  Ban,
  DollarSign,
  Gift,
  RotateCcw,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * One badge for every referral-engine status string — campaign
 * (draft/active/paused/ended), link (active/paused/disabled), and reward
 * ledger (pending/approved/granted/paid/rejected/clawed_back). Unknown values
 * fall back to a neutral pill rather than crashing.
 */
type BadgeConfig = { icon: React.ElementType; label: string; className: string; iconClassName: string };

const GRAY =
  'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700';
const GREEN =
  'bg-green-100 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-400 dark:border-green-800';
const AMBER =
  'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800';
const SLATE =
  'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
const RED =
  'bg-red-100 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-400 dark:border-red-800';
const BLUE =
  'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800';

const statusConfig: Record<string, BadgeConfig> = {
  // campaign + link
  draft: { icon: Clock, label: 'Draft', className: GRAY, iconClassName: 'text-gray-500' },
  active: { icon: CheckCircle, label: 'Active', className: GREEN, iconClassName: 'text-green-600' },
  paused: { icon: PauseCircle, label: 'Paused', className: AMBER, iconClassName: 'text-amber-600' },
  ended: { icon: XCircle, label: 'Ended', className: SLATE, iconClassName: 'text-slate-500' },
  disabled: { icon: Ban, label: 'Disabled', className: SLATE, iconClassName: 'text-slate-500' },
  // reward ledger
  pending: { icon: Clock, label: 'Pending', className: AMBER, iconClassName: 'text-amber-600' },
  approved: { icon: CheckCircle, label: 'Approved', className: BLUE, iconClassName: 'text-blue-600' },
  granted: { icon: Gift, label: 'Granted', className: GREEN, iconClassName: 'text-green-600' },
  paid: { icon: DollarSign, label: 'Paid', className: GREEN, iconClassName: 'text-green-600' },
  rejected: { icon: XCircle, label: 'Rejected', className: RED, iconClassName: 'text-red-600' },
  clawed_back: { icon: RotateCcw, label: 'Clawed back', className: RED, iconClassName: 'text-red-600' },
  // attribution
  attributed: { icon: CheckCircle, label: 'Attributed', className: BLUE, iconClassName: 'text-blue-600' },
  qualified: { icon: CheckCircle, label: 'Qualified', className: GREEN, iconClassName: 'text-green-600' },
  rewarded: { icon: Gift, label: 'Rewarded', className: GREEN, iconClassName: 'text-green-600' },
};

interface ReferralStatusBadgeProps {
  status: string | null | undefined;
  size?: 'sm' | 'md';
  showIcon?: boolean;
}

export function ReferralStatusBadge({ status, size = 'md', showIcon = true }: ReferralStatusBadgeProps) {
  const key = (status ?? '').toLowerCase();
  const config: BadgeConfig =
    statusConfig[key] ?? { icon: HelpCircle, label: status || 'Unknown', className: GRAY, iconClassName: 'text-gray-500' };
  const Icon = config.icon;

  const sizeClasses = { sm: 'px-2 py-0.5 text-xs gap-1', md: 'px-2.5 py-1 text-sm gap-1.5' };
  const iconSizes = { sm: 'h-3 w-3', md: 'h-3.5 w-3.5' };

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        sizeClasses[size],
        config.className,
      )}
    >
      {showIcon && <Icon className={cn(iconSizes[size], config.iconClassName)} />}
      {config.label}
    </span>
  );
}
